import { useState, useCallback } from 'react'
import { camelizeKeys } from 'humps'
import { receptionCustomService } from 'services'
import { CheckoutAdditionalField } from '../types'

type GetCheckoutAdditionalFieldsResponse = {
  checkoutAdditionalFields: CheckoutAdditionalField[]
}

type UseFetchCheckoutAdditionalFieldsOptions = {
  onSuccess?: (data: CheckoutAdditionalField[]) => void
  onError?: (error: Error) => void
}

type UseFetchCheckoutAdditionalFieldsReturn = {
  data: CheckoutAdditionalField[]
  isLoading: boolean
  error: Error | null
  fetchData: (tenantId: number | null) => Promise<void>
}

export const useFetchCheckoutAdditionalFields = (
  options?: UseFetchCheckoutAdditionalFieldsOptions
): UseFetchCheckoutAdditionalFieldsReturn => {
  const [data, setData] = useState<CheckoutAdditionalField[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const fetchData = useCallback(async (tenantId: number | null) => {
    if (!tenantId) return

    setIsLoading(true)

    try {
      const response = await receptionCustomService.getCheckoutAdditionalFields(tenantId)
      const { checkoutAdditionalFields } = camelizeKeys(response.data) as unknown as GetCheckoutAdditionalFieldsResponse
      setData(checkoutAdditionalFields)
      options?.onSuccess?.(checkoutAdditionalFields)
    } catch (err) {
      let errorMessage = 'データの取得に失敗しました'

      if (err instanceof Error) {
        errorMessage = err.message
      } else if (
        err &&
        typeof err === 'object' &&
        'error' in err &&
        typeof err.error === 'string'
      ) {
        errorMessage = err.error
      } else if (
        err &&
        typeof err === 'object' &&
        'message' in err &&
        typeof err.message === 'string'
      ) {
        errorMessage = err.message
      }

      const error = new Error(errorMessage)
      setError(error)
      options?.onError?.(error)
    } finally {
      setIsLoading(false)
    }
  }, [options])

  return {
    data,
    isLoading,
    error,
    fetchData,
  }
}
